import React, { useState, useEffect } from 'react';
import {
  User,
  Ticket,
  FileText,
  HelpCircle,
  MessageSquare,
  Plus,
  Clock,
  CheckCircle,
  AlertCircle,
  Download,
  ChevronRight,
  Phone,
  Mail,
  Calendar,
  CreditCard,
  Settings,
  Bell,
  BookOpen
} from 'lucide-react';

// Componente de ticket del cliente
const ClientTicketCard = ({ ticket }) => {
  const statusColors = {
    abierto: 'bg-blue-50 text-blue-700',
    en_proceso: 'bg-yellow-50 text-yellow-700',
    resuelto: 'bg-green-50 text-green-700'
  };

  const priorityBorder = {
    alta: 'border-l-red-500',
    media: 'border-l-yellow-500',
    baja: 'border-l-green-500'
  };

  return (
    <div className={`bg-white rounded-xl border border-gray-100 border-l-4 ${priorityBorder[ticket.prioridad]} p-4 hover:shadow-md transition-all cursor-pointer`}>
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-[#3B82F6]">#{ticket.id}</span>
            <span className={`px-2 py-0.5 rounded-lg text-xs font-medium ${statusColors[ticket.estado]}`}>
              {ticket.estado.replace('_', ' ')}
            </span>
          </div>
          <h4 className="font-medium text-[#111827] mt-1">{ticket.asunto}</h4>
        </div>
        <ChevronRight className="w-5 h-5 text-[#9CA3AF]" />
      </div>
      <div className="mt-3 flex items-center gap-4 text-xs text-[#6B7280]">
        <span className="flex items-center gap-1">
          <Clock className="w-3 h-3" />
          {ticket.fecha}
        </span>
        {ticket.tecnico && (
          <span className="flex items-center gap-1">
            <User className="w-3 h-3" />
            {ticket.tecnico}
          </span>
        )}
      </div>
    </div>
  );
};

// Componente de factura
const InvoiceRow = ({ invoice }) => {
  const statusColors = {
    pagada: 'bg-green-50 text-green-700',
    pendiente: 'bg-yellow-50 text-yellow-700',
    vencida: 'bg-red-50 text-red-700'
  };

  return (
    <div className="flex items-center justify-between p-4 hover:bg-gray-50 rounded-xl transition-colors">
      <div className="flex items-center gap-4">
        <div className="p-2 bg-gray-100 rounded-lg">
          <FileText className="w-5 h-5 text-[#6B7280]" />
        </div>
        <div>
          <p className="text-sm font-medium text-[#111827]">{invoice.numero}</p>
          <p className="text-xs text-[#6B7280]">{invoice.fecha}</p>
        </div>
      </div>
      <div className="flex items-center gap-4">
        <span className={`px-2 py-1 rounded-lg text-xs font-medium ${statusColors[invoice.estado]}`}>
          {invoice.estado}
        </span>
        <span className="font-semibold text-[#111827]">{invoice.importe}</span>
        <button className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
          <Download className="w-4 h-4 text-[#6B7280]" />
        </button>
      </div>
    </div>
  );
};

// Componente de articulo FAQ
const FAQItem = ({ faq }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="border-b border-gray-100 last:border-0">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50 transition-colors"
      >
        <span className="text-sm font-medium text-[#111827]">{faq.pregunta}</span>
        <ChevronRight className={`w-4 h-4 text-[#6B7280] transition-transform ${isOpen ? 'rotate-90' : ''}`} />
      </button>
      {isOpen && (
        <div className="px-4 pb-4 text-sm text-[#6B7280]">
          {faq.respuesta}
        </div>
      )}
    </div>
  );
};

export default function ClientesPanel() {
  const [activeTab, setActiveTab] = useState('resumen');
  const [loading, setLoading] = useState(true);

  const [clientInfo] = useState({
    nombre: 'Restaurante El Molino',
    email: 'contacto@elmolino.com',
    telefono: '612 345 678',
    plan: 'Profesional',
    proximaFactura: '1 Feb 2026',
    ticketsActivos: 2
  });

  const [tickets] = useState([
    { id: '1234', asunto: 'Problema con impresora de tickets', estado: 'en_proceso', prioridad: 'alta', fecha: 'Hoy 10:30', tecnico: 'Carlos R.' },
    { id: '1230', asunto: 'Consulta sobre facturacion', estado: 'abierto', prioridad: 'baja', fecha: 'Ayer', tecnico: null },
    { id: '1215', asunto: 'Actualizacion de software', estado: 'resuelto', prioridad: 'media', fecha: '12 Ene', tecnico: 'Ana M.' },
    { id: '1198', asunto: 'Configuracion nuevo terminal', estado: 'resuelto', prioridad: 'media', fecha: '5 Ene', tecnico: 'Miguel P.' }
  ]);

  const [invoices] = useState([
    { numero: 'FAC-2026-001', fecha: '1 Ene 2026', importe: '€145,00', estado: 'pagada' },
    { numero: 'FAC-2025-012', fecha: '1 Dic 2025', importe: '€145,00', estado: 'pagada' },
    { numero: 'FAC-2025-011', fecha: '1 Nov 2025', importe: '€145,00', estado: 'pagada' }
  ]);

  const [faqs] = useState([
    { pregunta: 'Como puedo cambiar mi contrasena?', respuesta: 'Para cambiar tu contrasena, ve a Configuracion > Seguridad > Cambiar contrasena. Necesitaras tu contrasena actual y la nueva contrasena debe tener al menos 12 caracteres.' },
    { pregunta: 'Como abro un ticket de soporte?', respuesta: 'Puedes abrir un ticket desde la seccion "Mis tickets" haciendo clic en el boton "Nuevo ticket". Describe tu problema con el mayor detalle posible para una resolucion mas rapida.' },
    { pregunta: 'Cual es el horario de soporte?', respuesta: 'Nuestro equipo de soporte esta disponible de lunes a viernes de 9:00 a 20:00 y sabados de 10:00 a 14:00. Para urgencias fuera de horario, usa el numero de emergencias.' },
    { pregunta: 'Como descargo mis facturas?', respuesta: 'Puedes descargar tus facturas desde la seccion "Facturas". Haz clic en el icono de descarga junto a cada factura para obtener el PDF.' }
  ]);

  const tabs = [
    { id: 'resumen', label: 'Resumen', icon: User },
    { id: 'tickets', label: 'Mis tickets', icon: Ticket },
    { id: 'facturas', label: 'Facturas', icon: FileText },
    { id: 'ayuda', label: 'Ayuda', icon: HelpCircle }
  ];

  useEffect(() => {
    const timer = setTimeout(() => setLoading(false), 500);
    return () => clearTimeout(timer);
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#3B82F6]"></div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-[#111827]">Portal Cliente</h1>
          <p className="text-[#6B7280] mt-1">Bienvenido, {clientInfo.nombre}</p>
        </div>
        <button className="flex items-center gap-2 px-4 py-2 bg-[#3B82F6] text-white rounded-xl hover:bg-[#2563EB] transition-colors">
          <Plus className="w-4 h-4" />
          <span>Nuevo ticket</span>
        </button>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 bg-gray-100 p-1 rounded-xl w-fit">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              activeTab === tab.id
                ? 'bg-white text-[#111827] shadow-sm'
                : 'text-[#6B7280] hover:text-[#111827]'
            }`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Content */}
      {activeTab === 'resumen' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Info cuenta */}
          <div className="lg:col-span-2 space-y-4">
            <div className="bg-white rounded-2xl border border-gray-100 p-6">
              <h2 className="text-lg font-semibold text-[#111827] mb-4">Informacion de cuenta</h2>
              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                  <div className="p-2 bg-blue-50 rounded-lg">
                    <User className="w-4 h-4 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-xs text-[#6B7280]">Empresa</p>
                    <p className="text-sm font-medium text-[#111827]">{clientInfo.nombre}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                  <div className="p-2 bg-green-50 rounded-lg">
                    <CreditCard className="w-4 h-4 text-green-600" />
                  </div>
                  <div>
                    <p className="text-xs text-[#6B7280]">Plan</p>
                    <p className="text-sm font-medium text-[#111827]">{clientInfo.plan}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                  <div className="p-2 bg-purple-50 rounded-lg">
                    <Mail className="w-4 h-4 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-xs text-[#6B7280]">Email</p>
                    <p className="text-sm font-medium text-[#111827]">{clientInfo.email}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                  <div className="p-2 bg-yellow-50 rounded-lg">
                    <Phone className="w-4 h-4 text-yellow-600" />
                  </div>
                  <div>
                    <p className="text-xs text-[#6B7280]">Telefono</p>
                    <p className="text-sm font-medium text-[#111827]">{clientInfo.telefono}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Tickets recientes */}
            <div className="bg-white rounded-2xl border border-gray-100 p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-[#111827]">Tickets recientes</h2>
                <button
                  onClick={() => setActiveTab('tickets')}
                  className="text-sm text-[#3B82F6] hover:underline"
                >
                  Ver todos
                </button>
              </div>
              <div className="space-y-3">
                {tickets.slice(0, 3).map((ticket) => (
                  <ClientTicketCard key={ticket.id} ticket={ticket} />
                ))}
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-4">
            {/* Resumen rapido */}
            <div className="bg-white rounded-2xl border border-gray-100 p-4">
              <h3 className="font-semibold text-[#111827] mb-4">Estado actual</h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 bg-blue-50 rounded-xl">
                  <div className="flex items-center gap-2">
                    <Ticket className="w-4 h-4 text-blue-600" />
                    <span className="text-sm text-[#111827]">Tickets activos</span>
                  </div>
                  <span className="font-semibold text-blue-600">{clientInfo.ticketsActivos}</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-[#6B7280]" />
                    <span className="text-sm text-[#111827]">Proxima factura</span>
                  </div>
                  <span className="font-medium text-[#111827]">{clientInfo.proximaFactura}</span>
                </div>
              </div>
            </div>

            {/* Contacto soporte */}
            <div className="bg-white rounded-2xl border border-gray-100 p-4">
              <h3 className="font-semibold text-[#111827] mb-4">Contactar soporte</h3>
              <div className="space-y-2">
                <button className="w-full flex items-center gap-3 p-3 hover:bg-gray-50 rounded-xl transition-colors text-left">
                  <div className="p-2 bg-green-50 rounded-lg">
                    <Phone className="w-4 h-4 text-green-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-[#111827]">Llamar</p>
                    <p className="text-xs text-[#6B7280]">900 123 456</p>
                  </div>
                </button>
                <button className="w-full flex items-center gap-3 p-3 hover:bg-gray-50 rounded-xl transition-colors text-left">
                  <div className="p-2 bg-blue-50 rounded-lg">
                    <MessageSquare className="w-4 h-4 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-[#111827]">Chat en vivo</p>
                    <p className="text-xs text-[#6B7280]">Disponible ahora</p>
                  </div>
                </button>
              </div>
            </div>

            {/* Acciones rapidas */}
            <div className="bg-white rounded-2xl border border-gray-100 p-4">
              <h3 className="font-semibold text-[#111827] mb-4">Acciones rapidas</h3>
              <div className="space-y-1">
                <button className="w-full flex items-center gap-3 p-2 hover:bg-gray-50 rounded-lg text-left text-sm text-[#6B7280]">
                  <Settings className="w-4 h-4" />
                  Configuracion cuenta
                </button>
                <button className="w-full flex items-center gap-3 p-2 hover:bg-gray-50 rounded-lg text-left text-sm text-[#6B7280]">
                  <Bell className="w-4 h-4" />
                  Notificaciones
                </button>
                <button className="w-full flex items-center gap-3 p-2 hover:bg-gray-50 rounded-lg text-left text-sm text-[#6B7280]">
                  <BookOpen className="w-4 h-4" />
                  Manuales y guias
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'tickets' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-[#111827]">Mis tickets</h2>
            <div className="flex items-center gap-2 text-sm text-[#6B7280]">
              <span>{tickets.filter(t => t.estado !== 'resuelto').length} abiertos</span>
              <span>·</span>
              <span>{tickets.filter(t => t.estado === 'resuelto').length} resueltos</span>
            </div>
          </div>
          <div className="space-y-3">
            {tickets.map((ticket) => (
              <ClientTicketCard key={ticket.id} ticket={ticket} />
            ))}
          </div>
        </div>
      )}

      {activeTab === 'facturas' && (
        <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
            <h2 className="font-semibold text-[#111827]">Historial de facturas</h2>
            <button className="text-sm text-[#3B82F6] hover:underline">
              Descargar todas
            </button>
          </div>
          <div className="divide-y divide-gray-50">
            {invoices.map((invoice, index) => (
              <InvoiceRow key={index} invoice={invoice} />
            ))}
          </div>
        </div>
      )}

      {activeTab === 'ayuda' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-100">
                <h2 className="font-semibold text-[#111827]">Preguntas frecuentes</h2>
              </div>
              {faqs.map((faq, index) => (
                <FAQItem key={index} faq={faq} />
              ))}
            </div>
          </div>

          <div className="space-y-4">
            <div className="bg-white rounded-2xl border border-gray-100 p-4">
              <h3 className="font-semibold text-[#111827] mb-4">Recursos</h3>
              <div className="space-y-2">
                <a href="#" className="flex items-center gap-3 p-3 hover:bg-gray-50 rounded-xl transition-colors">
                  <div className="p-2 bg-blue-50 rounded-lg">
                    <BookOpen className="w-4 h-4 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-[#111827]">Manual de usuario</p>
                    <p className="text-xs text-[#6B7280]">Guia completa del sistema</p>
                  </div>
                </a>
                <a href="#" className="flex items-center gap-3 p-3 hover:bg-gray-50 rounded-xl transition-colors">
                  <div className="p-2 bg-green-50 rounded-lg">
                    <HelpCircle className="w-4 h-4 text-green-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-[#111827]">Video tutoriales</p>
                    <p className="text-xs text-[#6B7280]">Aprende paso a paso</p>
                  </div>
                </a>
              </div>
            </div>

            <div className="bg-[#3B82F6] rounded-2xl p-4 text-white">
              <h3 className="font-semibold mb-2">Necesitas ayuda?</h3>
              <p className="text-sm text-blue-100 mb-4">
                Nuestro equipo esta disponible para ayudarte con cualquier duda.
              </p>
              <button className="w-full py-2 bg-white text-[#3B82F6] rounded-xl font-medium text-sm hover:bg-blue-50 transition-colors">
                Contactar soporte
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
